import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { Image, Spin } from 'antd'
import {
  CameraOutlined,
  EyeInvisibleOutlined,
  RightOutlined
} from '@ant-design/icons'

import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { listServicePhotosByCustomer } from '../api/servicePhoto'
import { toServicePhotoUrl } from './ServicePhotoThumbs'

/**
 * 顧客詳情「照片記錄」裡的施作照片「預覽」—— 結帳時上傳的照片自動出現在這裡。
 *
 * 只放最近 3 次、每次一列（照片橫向滑），高度固定，不會把顧客頁撐長；
 * 要看全部請按「查看全部施作照片」進 CustomerServicePhotosPage。
 *
 * 照片本體沒有複製進 Customer.photos，直接查 ServicePhoto：
 * 預約詳情刪掉一張、或改成不給客人看，這裡會同步。
 *
 * 日期／時間是結帳單上的牆上時間字串，直接顯示，不需要時區轉換。
 * 方案與權限自己判斷，沒開通就 return null。
 * onLoaded(total) 讓父層知道有沒有施作照片（決定要不要顯示「暫無照片」）。
 */

const PREVIEW_VISITS = 3

const Section = styled.div`
  margin-top: 8px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin: 12px 0 10px;

  .anticon {
    color: #d5728a;
  }
`

const SubNote = styled.span`
  font-weight: 400;
  color: #9ca3af;
  font-size: 12px;
`

const Row = styled.div`
  padding: 10px 12px;
  border-radius: 12px;
  background: #fafafb;
  border: 1px solid #f0f0f3;
  min-width: 0;

  & + & {
    margin-top: 8px;
  }
`

const RowHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
`

const RowDate = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #0f2562;
  white-space: nowrap;
`

const RowServices = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #4b5563;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Tag = styled.span`
  flex: none;
  font-size: 11px;
  font-weight: 600;
  color: #86868b;
  background: #f0f0f3;
  border-radius: 10px;
  padding: 1px 8px;
`

/* 備註最多兩行，完整內容到「全部施作照片」頁看 */
const Remarks = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`

/* 照片一列橫向滑，不往下長 */
const Strip = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 8px;
  overflow-x: auto;
  scrollbar-width: thin;
  padding-bottom: 2px;
`

const Thumb = styled.div`
  position: relative;
  flex: none;
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  background: #f4f5f7;

  .ant-image,
  .ant-image-img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    display: block;
    cursor: zoom-in;
  }
`

const HiddenBadge = styled.span`
  position: absolute;
  right: 3px;
  bottom: 3px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  pointer-events: none;
`

const AllButton = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 9px 12px;
  border: 1px solid #f5dfe7;
  border-radius: 10px;
  background: #fdf7f9;
  color: #c4596f;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;

  &:hover {
    background: #f9e8ee;
  }
`

const CustomerServicePhotoRecords = ({ businessId, customerId, onLoaded }) => {
  const navigate = useNavigate()
  const { hasFeature } = useMembership()
  const { canAny } = usePermission()
  const allowed =
    hasFeature('servicePhoto') &&
    canAny(['servicePhoto:view', 'servicePhoto:manage'])

  const [groups, setGroups] = useState([])
  const [summary, setSummary] = useState({ visits: 0, photos: 0 })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!allowed || !businessId || !customerId) {
      setGroups([])
      setSummary({ visits: 0, photos: 0 })
      onLoaded?.(0)
      return undefined
    }
    let alive = true
    setLoading(true)
    listServicePhotosByCustomer(businessId, customerId, {
      limit: PREVIEW_VISITS
    })
      .then((res) => {
        if (!alive) return
        const data = res?.data?.data || {}
        const s = data.summary || { visits: 0, photos: 0 }
        setGroups(Array.isArray(data.groups) ? data.groups : [])
        setSummary(s)
        onLoaded?.(s.photos || 0)
      })
      .catch(() => {
        // 靜默：照片撈不到不該讓整個顧客頁跳錯誤
        if (!alive) return
        setGroups([])
        setSummary({ visits: 0, photos: 0 })
        onLoaded?.(0)
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
    // onLoaded 是父層的 setState，不放進依賴避免每次 render 重撈
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, businessId, customerId])

  if (!allowed) return null
  if (loading && groups.length === 0) {
    return (
      <Section>
        <Spin size='small' />
      </Section>
    )
  }
  if (groups.length === 0) return null

  return (
    <Section>
      <Header>
        <CameraOutlined />
        最近施作
        <SubNote>（結帳時上傳的照片會自動帶入）</SubNote>
      </Header>

      {groups.map((group) => (
        <Row key={group.bookingId}>
          <RowHead>
            <RowDate>{group.date || '日期不明'}</RowDate>
            <RowServices title={(group.services || []).join('、')}>
              {(group.services || []).join('、')}
            </RowServices>
            {!group.checkedOut && <Tag>尚未結帳</Tag>}
            {group.orderStatus === 'voided' && <Tag>結帳已作廢</Tag>}
          </RowHead>
          {group.remarks ? (
            <Remarks title={group.remarks}>備註：{group.remarks}</Remarks>
          ) : null}
          <Image.PreviewGroup>
            <Strip>
              {group.photos.map((photo) => (
                <Thumb key={photo._id}>
                  <Image
                    src={toServicePhotoUrl(photo.previewUrl || photo.url)}
                    preview={{ src: toServicePhotoUrl(photo.url) }}
                    alt={photo.caption || '施作照片'}
                  />
                  {photo.visibleToCustomer === false && (
                    <HiddenBadge title='未對客人公開'>
                      <EyeInvisibleOutlined />
                    </HiddenBadge>
                  )}
                </Thumb>
              ))}
            </Strip>
          </Image.PreviewGroup>
        </Row>
      ))}

      <AllButton
        type='button'
        onClick={() => navigate(`/customers/${customerId}/service-photos`)}
      >
        查看全部施作照片（{summary.visits} 次・{summary.photos} 張）
        <RightOutlined />
      </AllButton>
    </Section>
  )
}

export default CustomerServicePhotoRecords
